import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  StackDivider,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { MessageTemplate } from "features/Resources/MessageTemplate/models";
import { useForm, useController } from "react-hook-form";
import { useNavigate } from "react-router";
import { useCreateMessageTemplate, useUpdateMessageTemplate } from "features/Resources/MessageTemplate/hooks";
import { PrimaryButton } from "components/Button";
import { dirtyValues } from "helpers/form";
import { toMessageTemplateFormData } from "features/Resources/MessageTemplate/helpers";
import ActionRichTextAreaField from "components/FormElements/Fields/TemplateRichTextAreaField";
import NameField from "features/Resources/Shared/Components/Fields/NameField";
import { Path } from "features/Path/models";
import TemplateResourceOptionMenu from "features/Resources/SharedTemplate/Components/TemplateResourceOptionMenu";
import { yupResolver } from "@hookform/resolvers/yup";
import { v2pathPathById } from "helpers/url/pages/v2/pathPath";
import { useCallback, useMemo, useRef, useState } from "react";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import TagByResourceType from "components/Tag/TagByResourceType";
import ArchivedTag from "components/Tag/ArchivedTag";
import BasePathTag from "components/Tag/BasePathTag";
import SelectPathsMenuButton from "features/Path/Components/Button/SelectPathsMenuButton";
import AttributeColumnLabel from "features/Resources/Shared/Components/AttributeColumnLabel";
import { PlusCirlceButtonWithRef } from "components/Button/PlusCircleButton";
import RecordTargetTag from "features/RecordTarget/RecordTargetTag";
import RecordTargetForm from "features/Resources/Shared/Components/Form/RecordTargetForm";
import { RecordTarget } from "features/RecordTarget/models";
import { Schema } from "features/Resources/Shared/Components/Form/RecordTargetForm/schema";
import SchedulingTriggerModalButton from "features/Resources/Shared/Components/Button/SchedulingTriggerModalButton";
import { AttributeButtonWithRef } from "components/Button/AttributeButton";
import BaseResourceTargetTag from "components/Tag/ResourceTargetTag/BaseResourceTargetTag";
import GenericSelectable, { ViewTypeOption } from "components/Selectable/GenericSelectable";
import { useCurrentUserQuery, useUserDetailQuery } from "features/User/hooks";
import { usePathsFieldArray } from "features/Resources/Shared/hooks/usePathsFieldArray";
import ComposeBox, { useComposeBoxColors, BORDERLESS_TRIX_SX } from "features/Resources/Shared/Components/ComposeBox";
import { messageTemplateSchema, MessageTemplateSchema } from "../schema";

const SENDER_OPTIONS: ViewTypeOption[] = [
  { value: "bot", label: "Camino Bot" },
  { value: "myself", label: "Myself" },
];

export default function MessageTemplateForm({ id, data }: { id: string; data: MessageTemplate }) {
  const toast = useToast();
  const navigate = useNavigate();
  const form = useForm<MessageTemplateSchema>({
    mode: "onChange",
    resolver: yupResolver(messageTemplateSchema),
    values: toMessageTemplateFormData(data),
    defaultValues: { paths: [] },
  });
  const { mutate: onUpdate } = useUpdateMessageTemplate(data?.id);
  const { mutate: onCreate } = useCreateMessageTemplate();
  const {
    handleSubmit,
    control,
    formState: { dirtyFields, errors },
  } = form;

  // Paths field logic - using shared hook
  const { pathFields, removePath, addPathId, selectedPathIds, getPathData, returnPathId } = usePathsFieldArray(form);

  // Paths field: Selected paths components
  const selectedPathsComponents = useMemo(
    () =>
      (pathFields || []).map((field: any, index: number) => {
        const path = getPathData(field.id);
        return (
          <BasePathTag
            key={field.fieldId}
            pathId={path?.id}
            pathName={path?.name}
            icon={path?.icon}
            iconColor={path?.iconColor}
            removeable
            onRemove={() => removePath(index)}
          />
        );
      }),
    [pathFields, getPathData, removePath],
  );

  // RecordTarget (To) field logic
  const { field: recipientField } = useController({ control, name: "recipient" });
  const {
    isOpen: isRecipientPopoverOpen,
    onOpen: onRecipientPopoverOpen,
    onClose: onRecipientPopoverClose,
    getDisclosureProps: getRecipientDisclosureProps,
  } = useDisclosure();
  const recipientDisclosureProps = getRecipientDisclosureProps();

  // MessageSender field logic
  const { data: currentUser } = useCurrentUserQuery();
  const existingSenderId = form.watch("senderId");
  const { data: existingSender } = useUserDetailQuery(existingSenderId || "", {
    enabled: !!existingSenderId,
  });
  const { isOpen: isSenderPopoverOpen, onOpen: onSenderPopoverOpen, onClose: onSenderPopoverClose } = useDisclosure();
  const { isOpen: isRemoveAlertOpen, onOpen: onRemoveAlertOpen, onClose: onRemoveAlertClose } = useDisclosure();
  const { isOpen: isChangeAlertOpen, onOpen: onChangeAlertOpen, onClose: onChangeAlertClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const changeRef = useRef<HTMLButtonElement>(null);
  const [pendingSenderValue, setPendingSenderValue] = useState<string | null>(null);
  const isEditingTemplate = id && id !== "new";

  // RecordTarget (To) field: Submit callback
  const onRecipientSubmit = useCallback(
    (recipientData: Schema) => {
      form.setValue("recipient", recipientData, { shouldValidate: true, shouldDirty: true });
      onRecipientPopoverClose();
    },
    [form, onRecipientPopoverClose],
  );

  // MessageSender field: Apply sender change
  const applySenderChange = useCallback(
    (value: string) => {
      if (value === "bot") {
        form.setValue("senderId", null, { shouldDirty: true });
      } else if (value === "myself" && currentUser) {
        form.setValue("senderId", currentUser.id, { shouldDirty: true });
      }
    },
    [form, currentUser],
  );

  // MessageSender field: Handle select option
  const handleSelectOption = useCallback(
    (value: string) => {
      if (isEditingTemplate) {
        setPendingSenderValue(value);
        onSenderPopoverClose();
        onChangeAlertOpen();
      } else {
        applySenderChange(value);
        onSenderPopoverClose();
      }
    },
    [isEditingTemplate, applySenderChange, onSenderPopoverClose, onChangeAlertOpen],
  );

  // MessageSender field: Handle remove sender
  const handleRemoveSender = useCallback(() => {
    if (isEditingTemplate) {
      onRemoveAlertOpen();
    } else {
      form.setValue("senderId", null, { shouldDirty: true });
    }
  }, [isEditingTemplate, form, onRemoveAlertOpen]);

  // MessageSender field: Handle confirm change
  const handleConfirmChange = useCallback(() => {
    if (pendingSenderValue) {
      applySenderChange(pendingSenderValue);
      setPendingSenderValue(null);
    }
    onChangeAlertClose();
  }, [pendingSenderValue, applySenderChange, onChangeAlertClose]);

  // MessageSender field: Handle cancel change
  const handleCancelChange = useCallback(() => {
    setPendingSenderValue(null);
    onChangeAlertClose();
  }, [onChangeAlertClose]);

  // MessageSender field: Handle confirm remove
  const handleConfirmRemove = useCallback(() => {
    form.setValue("senderId", null, { shouldDirty: true });
    onRemoveAlertClose();
  }, [form, onRemoveAlertClose]);

  // Compose box colors for the body bg + hover (matches MessageForm).
  const { bodyBg: composeBodyBg, bodyHoverBg: composeBodyHoverBg } = useComposeBoxColors();

  const onSubmit = (formData: Partial<MessageTemplateSchema>) => {
    // For new templates, use all form data. For updates, use only dirty fields.
    const submitData = id === "new" ? formData : dirtyValues(formData, dirtyFields);

    // Early return if updating with no changes
    if (id !== "new" && (!submitData || Object.keys(submitData).length === 0)) {
      return;
    }

    // Convert paths to pathIds if present
    if (submitData.paths) {
      submitData.pathIds = ((submitData?.paths || []) as Path[]).map((path) => path.id);
    }

    if (id === "new") {
      onCreate(submitData, {
        onSuccess: (res: any) => {
          toast({ title: "Message template created.", status: "success" });
          if (returnPathId) {
            navigate(v2pathPathById(returnPathId));
          } else if (formData?.pathIds?.length) {
            navigate(v2pathPathById(formData.pathIds[0]));
          } else {
            navigate(PEOPLE_TEAM_ROUTES.messageTemplates.index);
          }
        },
      });
    } else {
      onUpdate(submitData, { onSuccess: () => toast({ title: "Message template updated.", status: "success" }) });
    }
  };

  return (
    <>
      <VStack spacing={4} align="stretch" width="100%">
        {/* Top row: full width */}
        <Flex w="100%" justify="space-between" alignItems="center">
          <TagByResourceType type="message_template" />
          <HStack>
            {data?.archivedAt && <ArchivedTag />}
            <PrimaryButton
              onClick={handleSubmit(onSubmit, () => {
                toast({ title: "Please fix the errors before continuing.", status: "error", duration: 5000 });
              })}
            >
              {id === "new" ? "Add" : "Save"}
            </PrimaryButton>
            {data && <TemplateResourceOptionMenu template={data} />}
          </HStack>
        </Flex>

        {/* Name above the attributes row. */}
        <NameField form={form} />

        {/* Template attributes — Sender | Send At | Path, framed top/bottom,
            vertical dividers between, side-by-side at the left. */}
        <HStack
          divider={<StackDivider borderColor="gray.200" />}
          spacing={0}
          py={4}
          borderTop="1px solid"
          borderBottom="1px solid"
          borderColor="gray.200"
          align="stretch"
          justify="flex-start"
        >
          <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="220px">
            <AttributeColumnLabel>Sender</AttributeColumnLabel>
            <Flex gap="1" align="center">
              {existingSenderId && existingSender ? (
                <BaseResourceTargetTag name={existingSender.fullName} remove={handleRemoveSender} />
              ) : (
                <Popover isOpen={isSenderPopoverOpen} onOpen={onSenderPopoverOpen} onClose={onSenderPopoverClose}>
                  <PopoverTrigger>
                    <AttributeButtonWithRef bg="bg.control">Camino Bot</AttributeButtonWithRef>
                  </PopoverTrigger>
                  <PopoverContent
                    pt="3"
                    bg="var(--chakra-colors-chakra-body-bg)"
                    minW={{ base: "100%", lg: "max-content" }}
                    width="100%"
                    maxWidth="350px"
                  >
                    <PopoverBody>
                      <GenericSelectable
                        value={existingSenderId ? "myself" : "bot"}
                        setValue={handleSelectOption}
                        options={SENDER_OPTIONS}
                      />
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              )}
            </Flex>
          </VStack>
          <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="220px">
            <AttributeColumnLabel>Send At</AttributeColumnLabel>
            <FormControl isInvalid={!!errors.deliverAtTrigger}>
              <SchedulingTriggerModalButton
                form={form}
                label="Send At"
                fieldName="deliverAtTrigger"
                timingOnly={id === "new"}
                allowWeekendsFieldName="allowWeekends"
              />
              <FormErrorMessage mt={0} fontSize="xs">
                {(errors.deliverAtTrigger as any)?.message}
              </FormErrorMessage>
            </FormControl>
          </VStack>
          <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="220px">
            <AttributeColumnLabel>Path</AttributeColumnLabel>
            <Flex gap="1" align="center" wrap="wrap">
              {selectedPathsComponents}
              <SelectPathsMenuButton selectedPathIds={selectedPathIds} addPathId={addPathId} />
            </Flex>
          </VStack>
        </HStack>

        <Flex align="start" width="100%">
          {/* Message composer — shared ComposeBox with To header row + Trix
              body. The toolbar's personalization `{}` button is wired below
              to open a token-picker modal. */}
          <VStack flex="1" spacing={0} align="stretch" minW={0} pb={6}>
            <ComposeBox>
              <ComposeBox.HeaderRow label="To">
                <Popover
                  matchWidth
                  closeOnBlur={false}
                  {...recipientDisclosureProps}
                  onOpen={onRecipientPopoverOpen}
                  onClose={onRecipientPopoverClose}
                >
                  <FormControl isInvalid={!!errors.recipient}>
                    <Flex gap="1" align="center" wrap="wrap">
                      {recipientField.value ? (
                        <RecordTargetTag
                          key={recipientField.value.id}
                          recordTarget={recipientField.value as unknown as RecordTarget}
                          remove={() => form.setValue("recipient", null, { shouldDirty: true })}
                        />
                      ) : (
                        <PopoverTrigger>
                          <PlusCirlceButtonWithRef label="Add To" />
                        </PopoverTrigger>
                      )}
                    </Flex>
                    <FormErrorMessage mt={0} fontSize="xs">{(errors.recipient as any)?.message}</FormErrorMessage>
                  </FormControl>
                  <PopoverContent
                    pt="3"
                    bg="var(--chakra-colors-chakra-body-bg)"
                    minW={{ base: "100%", lg: "max-content" }}
                    width="100%"
                    maxWidth="350px"
                    display={isRecipientPopoverOpen ? "block" : "none"}
                  >
                    <PopoverCloseButton size="xs" sx={{ _hover: { bg: "transparent", color: "fg.emphasized" } }} />
                    <PopoverBody>
                      <RecordTargetForm label="To" onSubmit={onRecipientSubmit} recordTargetTypes="All" />
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </ComposeBox.HeaderRow>
              <Box
                px={4}
                pt={3}
                pb={2}
                bg={composeBodyBg}
                transition="background-color 0.15s"
                sx={{
                  ...BORDERLESS_TRIX_SX,
                  "&:hover:not(:focus-within)": { bg: composeBodyHoverBg },
                }}
              >
                <ActionRichTextAreaField
                  name="body"
                  label="Body"
                  control={control}
                  actionType="message"
                  alwaysShowToolbar
                  transparent
                  placeholder="Write the message…"
                />
              </Box>
            </ComposeBox>
          </VStack>
        </Flex>
      </VStack>

      {/* MessageSender AlertDialogs */}
      <AlertDialog isOpen={isRemoveAlertOpen} leastDestructiveRef={cancelRef} onClose={onRemoveAlertClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Change Message Sender
            </AlertDialogHeader>

            <AlertDialogBody>
              When you save, this will update the sender to Camino Bot for this template and all pending messages
              created from it.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onRemoveAlertClose}>
                Nevermind
              </Button>
              <Button colorScheme="blue" onClick={handleConfirmRemove} ml={3}>
                I Understand
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={isChangeAlertOpen} leastDestructiveRef={changeRef} onClose={handleCancelChange}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Change Message Sender
            </AlertDialogHeader>

            <AlertDialogBody>
              When you save, this will update the sender for this template and all pending messages created from it.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={changeRef} onClick={handleCancelChange}>
                Nevermind
              </Button>
              <Button colorScheme="blue" onClick={handleConfirmChange} ml={3}>
                I Understand
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
